import React, { useEffect, useMemo, useState, useCallback } from "react";
import Select from "react-select";
import WindowedSelect from "react-windowed-select";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  FaEye,
  FaFilter,
  FaRotateLeft,
  FaChevronLeft,
  FaXmark,
} from "react-icons/fa6";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  Drawer,
  Grid,
  CircularProgress,
} from "@mui/material";

import Pagination from "../Common/Pagination/Pagination";
import { AllCandidateL } from "../../Redux/action/AllCandidateListAction";
import {
  fetchDesignationDetails,
  fetchSkillDetails,
} from "../../Redux/action/MasterSkillAction";
import {
  fetchCountriesData,
  getAllCountries,
  getStatesByCountry,
} from "../../Services/GetLocation";
import Loader from "../Common/Loader/loader";

const PRIMARY_COLOR = "#2d6d96";
const TEXT_DARK = "#111827";
const POPIINS = "'Poppins', sans-serif";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "45px",
    borderRadius: "8px",
    fontFamily: POPIINS,
    fontSize: "14px",
    border: state.isFocused
      ? `2px solid ${PRIMARY_COLOR}`
      : "1px solid #d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: PRIMARY_COLOR },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  placeholder: (base) => ({ ...base, color: "#6b7280", fontWeight: 500 }),
  option: (base, state) => ({
    ...base,
    fontFamily: POPIINS,
    fontSize: "14px",
    backgroundColor: state.isSelected
      ? PRIMARY_COLOR
      : state.isFocused
        ? `${PRIMARY_COLOR}10`
        : "transparent",
    color: state.isSelected ? "white" : TEXT_DARK,
  }),
};

const getInitials = (name) => {
  if (!name) return "C";
  const parts = name.trim().split(/[ \-_]/);
  return parts.length === 1
    ? parts[0].substring(0, 2).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const generateExperienceOptions = (start = 0, label = "Select") => {
  const options = [{ value: "", label }];
  for (let i = start; i <= 31; i++) {
    options.push({ value: i, label: i.toString() });
  }
  return options;
};

const CandidateRow = React.memo(({ item }) => (
  <TableRow sx={{ whiteSpace: "nowrap" }} hover>
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50px",
            bgcolor: PRIMARY_COLOR,
            fontWeight: 700,
            fontSize: "14px",
            fontFamily: POPIINS,
          }}
        >
          {getInitials(item.candidateEmpId)}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ fontWeight: 800, color: PRIMARY_COLOR, fontFamily: POPIINS }}
        >
          {item.candidateEmpId}
        </Typography>
      </Box>
    </TableCell>
    <TableCell sx={{ fontFamily: POPIINS, fontSize: "13px", fontWeight: 600 }}>
      {item.currentDesignation || "—"}
    </TableCell>
    <TableCell sx={{ fontFamily: POPIINS, fontSize: "13px", fontWeight: 600 }}>
      {item.department || "—"}
    </TableCell>
    <TableCell
      sx={{ fontFamily: POPIINS, fontWeight: 700, color: PRIMARY_COLOR }}
    >
      {(() => {
        const years = parseInt(item?.keyExperience) || 0;
        const months = parseInt(item?.keyExperienceInMonth) || 0;
        if (years === 0 && months === 0) return "Fresher";
        const yearsText =
          years > 0 ? `${years} Year${years > 1 ? "s" : ""}` : "";
        const monthsText =
          months > 0 ? `${months} Month${months > 1 ? "s" : ""}` : "";
        return yearsText && monthsText
          ? `${yearsText} - ${monthsText}`
          : yearsText || monthsText;
      })()}
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          whiteSpace: "nowrap",
          flexWrap: "wrap",
        }}
      >
        {item.skills?.slice(0, 3).map((s, i) => (
          <Chip
            key={i}
            label={s}
            size="small"
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: POPIINS,
            }}
          />
        ))}

        {item.skills?.length > 3 && (
          <Tooltip title={item.skills.slice(3).join(", ")}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: PRIMARY_COLOR,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              +{item.skills.length - 3}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </TableCell>

    <TableCell sx={{ fontFamily: POPIINS, fontSize: "13px" }}>
      {[item.city, item.state, item.country].filter(Boolean).join(", ")}
    </TableCell>

    <TableCell sx={{ fontFamily: POPIINS, fontSize: "13px" }}>
      {item.workPreference ? (
        <Chip
          label={item.workPreference}
          size="small"
          sx={{
            fontSize: "11px",
            height: "22px",
            fontWeight: 500,
            color: "#fff",
            textTransform: "uppercase",
            background:
              item.workPreference === "REMOTE"
                ? "linear-gradient(135deg, #2ecc71, #27ae60)"
                : item.workPreference === "HYBRID"
                  ? "linear-gradient(135deg, #f39c12, #f1c40f)"
                  : "linear-gradient(135deg, #2d6d96, #2e86c1)",
            boxShadow: "0 3px 8px rgba(0,0,0,0.25)",
          }}
        />
      ) : (
        "-"
      )}
    </TableCell>

    <TableCell align="center">
      <IconButton
        component={Link}
        to={`/vendor/view_candidate/${item.candidateId}`}
        sx={{
          bgcolor: "#f3f4f6",
          color: PRIMARY_COLOR,
          "&:hover": { bgcolor: PRIMARY_COLOR, color: "#fff !important" },
        }}
      >
        <FaEye size={16} />
      </IconButton>
    </TableCell>
  </TableRow>
));

const AllCandidateList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showFilters, setShowFilters] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [search, setSearch] = useState("");
  const [skills, setSkills] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [minExp, setMinExp] = useState({ value: "", label: "Min" });
  const [maxExp, setMaxExp] = useState({ value: "", label: "Max" });
  const [loading, setLoading] = useState(false);

  const { userResult } = useSelector((state) => state.Auth);
  const vendorId = userResult?.vendorid;
  const skillList =
    useSelector((state) => state?.MasterTableDetails?.skill?.content) || [];
  const designationList =
    useSelector((state) => state?.MasterTableDetails?.designation?.content) ||
    [];
  const userInfo =
    useSelector((state) => state?.AllCandidateListData?.result?.content) || [];
  const data = useSelector((state) => state?.AllCandidateListData?.result);

  const handleReset = useCallback(() => {
    setSearch("");
    setSkills([]);
    setDesignation([]);
    setSelectedCountry(null);
    setSelectedState([]);
    setSelectedCity([]);
    setMinExp({ value: "", label: "Min" });
    setMaxExp({ value: "", label: "Max" });
    setPageNumber(0);
  }, []);

  const toggleMobileDrawer = () => setMobileOpen(!mobileOpen);

  useEffect(() => {
    dispatch(
      AllCandidateL({
        vendorId,
        pageNumber,
        search,
        encodedData: skills.map((s) => encodeURIComponent(s.value)),
        citySendApi: selectedCity.map((c) => c.value),
        countrySendApi: selectedCountry ? [selectedCountry.value] : [],
        stateSendApi: selectedState.map((s) => s.value),
        designationSendapi: designation.map((d) => d.value),
        min: minExp.value,
        max: maxExp.value,
      }),
    );
  }, [
    search,
    skills,
    selectedCity,
    selectedCountry,
    selectedState,
    designation,
    minExp,
    maxExp,
    pageNumber,
    vendorId,
    dispatch,
  ]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        dispatch(fetchSkillDetails());
        dispatch(fetchDesignationDetails());

        const [citiesRes, countriesRes] = await Promise.all([
          fetchCountriesData(),
          getAllCountries(),
        ]);

        setCities(citiesRes?.data?.data || []);
        setCountries(countriesRes?.data || []);
      } catch (error) {
        console.error("Failed to load initial data", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [dispatch]);

  useEffect(() => {
    const country = selectedCountry?.value || "India";
    getStatesByCountry(country).then((res) =>
      setStates(res?.data?.states || []),
    );
  }, [selectedCountry]);

  const skillOptions = useMemo(
    () => skillList.map((s) => ({ value: s.skillName, label: s.skillName })),
    [skillList],
  );

  const designationOptions = useMemo(
    () =>
      designationList.map((d) => ({
        value: d.designation,
        label: d.designation,
      })),
    [designationList],
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.country, label: c.country })),
    [countries],
  );

  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.name, label: s.name })),
    [states],
  );

  const cityOptions = useMemo(
    () =>
      [
        ...new Set(
          cities
            .filter(Boolean)
            .map((c) =>
              c
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-zA-Z\s.-]/g, "")
                .replace(/\s+/g, " ")
                .trim(),
            )
            .filter(Boolean),
        ),
      ]
        .sort()
        .map((city) => ({ value: city, label: city })),
    [cities],
  );

  // const minExpOptions = useMemo(() => generateExperienceOptions(0, "Min"), []);
  // const maxExpOptions = useMemo(() => generateExperienceOptions(0, "Max"), []);

  const FilterContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, fontFamily: POPIINS, color: TEXT_DARK }}
          >
            Filters
          </Typography>
          <IconButton
            onClick={
              isMobile ? toggleMobileDrawer : () => setShowFilters(false)
            }
            size="small"
          >
            {isMobile ? <FaXmark /> : <FaChevronLeft />}
          </IconButton>
        </Box>
        <Button
          fullWidth
          onClick={handleReset}
          startIcon={<FaRotateLeft size={12} />}
          variant="outlined"
          sx={{
            mb: 1,
            color: PRIMARY_COLOR,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
          }}
        >
          Reset
        </Button>
        <Divider />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            Search Candidate
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter candidate id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            Skills
          </Typography>
          <Select
            isMulti
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={skillOptions}
            value={skills}
            onChange={setSkills}
            placeholder="Skills"
            isClearable
          />
        </Box>
        <Box width="100%">
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 1, fontFamily: POPIINS }}
          >
            Experience
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Select
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={generateExperienceOptions(0, "Min")}
                value={minExp}
                onChange={setMinExp}
                placeholder="Min"
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Select
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={generateExperienceOptions(0, "Max")}
                value={maxExp}
                onChange={setMaxExp}
                placeholder="Max"
              />
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            Country
          </Typography>
          <Select
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={countryOptions}
            
            value={selectedCountry}
            onChange={setSelectedCountry}
            placeholder="Country"
            isClearable
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            State
          </Typography>
          <WindowedSelect
            isMulti
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={stateOptions}
            value={selectedState}
            onChange={setSelectedState}
            placeholder="States"
            isClearable
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            City
          </Typography>
          <WindowedSelect
            isMulti
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={cityOptions}
            value={selectedCity}
            onChange={setSelectedCity}
            placeholder="Cities"
            isClearable
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: POPIINS }}
          >
            Designation
          </Typography>
          <Select
            isMulti
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={designationOptions}
            value={designation}
            onChange={setDesignation}
            placeholder="Role"
            isClearable
          />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        overflow: "hidden",
        fontFamily: POPIINS,
      }}
    >
      <Grid container sx={{ height: "100vh" }}>
        {!isMobile && showFilters && (
          <Grid
            item
            md={3}
            lg={2.5}
            sx={{
              borderRight: "1px solid #e5e7eb",
              height: "100%",
              bgcolor: "#fff",
            }}
          >
            {FilterContent}
          </Grid>
        )}

        <Drawer anchor="left" open={mobileOpen} onClose={toggleMobileDrawer}>
          <Box sx={{ width: "280px", height: "100%" }}>{FilterContent}</Box>
        </Drawer>

        <Grid
          item
          xs={12}
          md={showFilters ? 9 : 12}
          lg={showFilters ? 9.5 : 12}
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {(isMobile || !showFilters) && (
              <IconButton
                onClick={
                  isMobile ? toggleMobileDrawer : () => setShowFilters(true)
                }
                sx={{
                  bgcolor: PRIMARY_COLOR,
                  color: "#fff",
                  "&:hover": { bgcolor: "#1a4a6b" },
                }}
              >
                <FaFilter size={14} />
              </IconButton>
            )}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: TEXT_DARK,
                  fontFamily: POPIINS,
                  fontSize: { xs: "1.1rem", md: "1.4rem" },
                }}
              >
                Candidate Directory
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#6b7280", fontFamily: POPIINS }}
              >
                Showing {data?.totalElements || 0} qualified profiles
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              px: { xs: 1, md: 3 },
              pb: 2,
              overflow: "hidden",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ whiteSpace: "nowrap" }}>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        CANDIDATE ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        DESIGNATION
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        DEPARTMENT
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        EXPERIENCE
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        SKILLS
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        LOCATION
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                      >
                        PREFERENCE
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 800,
                          fontFamily: POPIINS,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                        }}
                        align="center"
                      >
                        ACTION
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userInfo.length > 0 ? (
                      userInfo.map((item) => (
                        <CandidateRow key={item.candidateId} item={item} />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            py: 8,
                            color: "#6b7280",
                            fontFamily: POPIINS,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600, color: "#374151" }}
                            >
                              No candidates found
                            </Typography>
                            <Typography variant="body2">
                              Try adjusting your filters or search criteria
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "#fff",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <Pagination
                  totalPages={data?.totalPages || 0}
                  currentPage={pageNumber}
                  onPageChange={setPageNumber}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AllCandidateList;
